import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { MerchantHomeBindingCard } from "./ui/MerchantHomeBindingCard";
import { merchantHomeBindingApi, useGetMerchantHomeBindingQuery, useRegisterMerchantHomeBindingMutation, useSelectMerchantHomeBindingMutation } from "./merchantHomeBindingApi";
import type { MerchantHomeBindingPart } from "./merchantHomeBindingSchema";
import { useStoreBindingOAuth } from "@/features/settings/useStoreBindingOAuth";
import { activeStoreSelected } from "@/features/settings/activeStoreSlice";
import { baseApi } from "@/shared/api/baseApi";

const names = { shopify: "Shopify", shoplazza: "Shoplazza", shopee: "Shopee", amazon_sp: "Amazon", tiktok_shop: "TikTok Shop" };
export function ConversationStoreBindingCard({ part, organizationId, conversationId }: { part: MerchantHomeBindingPart; organizationId: string; conversationId: string }) {
  const userId = useAppSelector(state => state.authSession.actor?.userId ?? "");
  const scope = { userId, organizationId, conversationId, cardId: part.data.card_id };
  const dispatch = useAppDispatch();
  const cached = merchantHomeBindingApi.endpoints.getMerchantHomeBinding.useQueryState(scope);
  const polling = !cached.currentData || ["waiting", "confirming"].includes(cached.currentData.state) ? 2000 : 0;
  const query = useGetMerchantHomeBindingQuery(scope, { pollingInterval: polling, skipPollingIfUnfocused: true, refetchOnFocus: true });
  const [register] = useRegisterMerchantHomeBindingMutation();
  const [select, selecting] = useSelectMerchantHomeBindingMutation();
  const [selectionError, setSelectionError] = useState("");
  const autoSelected = useRef("");
  const flow = query.currentData;
  const oauth = useStoreBindingOAuth({
    organizationId,
    onPrepared: async prepared => { await register({ ...scope, operationId: prepared.operationId }).unwrap(); await query.refetch().unwrap(); },
    verify: async operationId => {
      const result = await query.refetch().unwrap();
      if (result.operation_id !== operationId || result.state !== "succeeded") throw new Error("binding_result_unconfirmed");
      dispatch(baseApi.util.invalidateTags([{ type: "MerchantStoreBindings", id: organizationId }]));
      return "店铺授权已完成，正在核对同步与会话店铺";
    },
  });
  const refetch = query.refetch;
  const operationId = flow?.operation_id ?? "";
  const handleSelect = useCallback(async (shopBindingId: string) => {
    if (!operationId) return;
    setSelectionError("");
    try { const result = await select({ userId, organizationId, conversationId, cardId: part.data.card_id, operationId, shopBindingId }).unwrap(); if (result.selected_shop_id) dispatch(activeStoreSelected({ organizationId, storeBindingId: result.selected_shop_id })); await refetch().unwrap(); }
    catch { setSelectionError("会话店铺未能确认，请重新读取结果。已有会话店铺会保留。"); }
  }, [dispatch, select, operationId, userId, organizationId, conversationId, part.data.card_id, refetch]);
  useEffect(() => {
    if (!flow) return;
    const shop = flow.shops[0];
    if (flow.state === "succeeded" && !flow.continued && (flow.conversation_has_shop || (flow.shops.length === 1 && shop)) && autoSelected.current !== flow.operation_id) {
      autoSelected.current = flow.operation_id;
      void handleSelect(flow.conversation_has_shop ? "" : shop?.id ?? "");
    }
    // 单店恢复由 operation ID 幂等控制，失败后展示显式重试。
  }, [flow, handleSelect]);
  const state = query.isError ? "failed" : oauth.work.status === "running" ? "waiting" : flow?.state === "succeeded" ? "succeeded" : oauth.work.status === "failed" ? "failed" : flow?.state ?? "confirming";
  const selectedShop = flow?.shops.find(shop => shop.id === flow.selected_shop_id) ?? (flow?.shops.length === 1 ? flow.shops[0] : undefined);
  const authorize = () => { setSelectionError(""); void oauth.start(part.data.provider, names[part.data.provider], part.data.shop_domain); };
  return <div>
    <MerchantHomeBindingCard platform={names[part.data.provider]} domain={part.data.shop_domain} state={state} operationId={flow?.operation_id} syncState={selectedShop?.sync_state}
      shops={flow?.conversation_has_shop && !flow.selected_shop_id ? [] : flow?.shops}
      selectedShopId={flow?.selected_shop_id} isSelectingShop={selecting.isLoading}
      message={query.isError ? "绑定状态暂时无法读取，请重新读取后继续。" : selectionError || (flow?.conversation_has_shop && !flow.selected_shop_id && flow.state === "succeeded" ? "店铺已绑定，当前会话原有店铺保持不变；顶部店铺菜单只影响下一段对话。" : undefined)}
      onAuthorize={flow?.state === "ready" ? authorize : undefined}
      onRetry={query.isError ? () => { void query.refetch(); } : authorize}
      onSelectShop={shopId => { void handleSelect(shopId); }} />
    {selectionError && <button type="button" onClick={() => { autoSelected.current = ""; void query.refetch(); }}>重新读取绑定结果</button>}
  </div>;
}
